import type JCE from '@chesscom/js-chess-engine';
import type { Markings } from 'chessboard/plugins/markings/config/types';

export namespace Effects {
  export namespace Plugin {
    export const Name = 'effects';
  }

  export enum BadgeSize {
    Normal = '18.75%',
    Large = '24.5%',
  }

  export enum Events {
    FlyoutEffectBegin = 'FlyoutEffectBegin',
    FlyoutEffectEnd = 'FlyoutEffectEnd',
    FadeOutEffectBegin = 'FadeOutEffectBegin',
    FadeOutEffectEnd = 'FadeOutEffectEnd',
  }

  export interface API {
    fadeOutOne(square: JCE.SanSquare): void;
  }

  /**
   * Properties used for Puzzles V2 animations
   */
  export type CustomEffect = {
    /** Text to display on the pill (e.g. "Extra Hard", "Nice!", "Streak 5") */
    pillText?: string;

    /** Text that appears on the square and then slides to the corner before flying out (e.g. "+5", "+15", "+10") */
    iconText?: string;

    /** Hex value for the square and flying element background color (e.g. "rgba(93, 153, 72, 1)", "#69923e") */
    backgroundColor?: string;

    /** Hex value for the initial text and flying element background color (e.g. "rgba(93, 153, 72, 1)", "#69923e") */
    foregroundColor?: string;

    /** The padding% used for the badge. You can use predefined sizes available from Effects.BadgeSize */
    badgeSize?: string;

    /** Hex value used to override the default background color of the pill because the pill uses inverted back and foreground by default (e.g. "rgba(93, 153, 72, 1)", "#69923e")*/
    pillBackgroundColor?: string;

    /** Hex value used to override the default foreground color of the pill because the pill uses inverted back and foreground by default (e.g. "rgba(93, 153, 72, 1)", "#69923e")*/
    pillForegroundColor?: string;
  };
}

export const EffectType = 'effect';

export type EffectParams = {
  data: EffectData;
  type: typeof EffectType;
};

export type Effect = Markings.Model & EffectParams;

export type KeyedEffect = Markings.KeyedModel & EffectParams;

export type EffectData = Markings.WithSquare &
  Effects.CustomEffect & {
    type: EffectTypes;
    path?: string;
    size?: string;
    animated?: boolean;
    flyOut?: {
      target: {
        x: number;
        y: number;
      };
    };
  };

export enum EffectTypes {
  Abandon = 'Abandon',
  Alternative = 'Alternative',
  BestMove = 'BestMove',
  Blunder = 'Blunder',
  Book = 'Book',
  Brilliant = 'Brilliant',
  BughouseWhite = 'BughouseWhite',
  BughouseBlack = 'BughouseBlack',
  CheckmateBlack = 'CheckmateBlack',
  CheckmateWhite = 'CheckmateWhite',
  Correct = 'Correct',
  Critical = 'Critical',
  Custom = 'Custom',
  DefeatBlack = 'DefeatBlack',
  DefeatWhite = 'DefeatWhite',
  DrawBlack = 'DrawBlack',
  DrawWhite = 'DrawWhite',
  Excellent = 'Excellent',
  FastWin = 'FastWin',
  Forced = 'Forced',
  FreePiece = 'FreePiece',
  Gamechanger = 'Gamechanger',
  Good = 'Good',
  GreatFind = 'GreatFind',
  Inaccuracy = 'Inaccuracy',
  Incorrect = 'Incorrect',
  Mate = 'Mate',
  Miss = 'Miss',
  MissedWin = 'MissedWin',
  Mistake = 'Mistake',
  ResignBlack = 'ResignBlack',
  ResignWhite = 'ResignWhite',
  Sharp = 'Sharp',
  Stalemate = 'Stalemate',
  Takeback = 'Takeback',
  Threat = 'Threat',
  TimeoutBlack = 'TimeoutBlack',
  TimeoutWhite = 'TimeoutWhite',
  Undo = 'Undo',
  Winner = 'Winner',
  WinnerWhite = 'WinnerWhite',
  Interesting = 'Interesting',
  Warning = 'Warning',
  Equal = 'Equal',
}

export type EffectAssertion = Partial<Record<JCE.SanSquare, Omit<EffectData, 'square'>>>;
